import * as _ from 'underscore';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType, SearchType } from '../../../models/enums';
import { NavigationInfo } from '../../../models/navigation-info';
import { SearchResult } from '../../../models/search-result';
import { Workflow, WorkflowStep } from '../../../models/workflow';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { PermissionSet, ObjectActionListType, ActionType } from '../../../models/permission';

function isWorkFlowRunning(item: GenericTreeNode | Workflow): boolean {
    let notRunning = true;
    if (item instanceof GenericTreeNode && item.data && item.data instanceof Workflow && item.data.attributes) {
        const attr = _.find(item.data.attributes, (attribute) => attribute.name === 'isRunning');
        if (attr) {
            notRunning = attr.value !== 'true';
        }
    }
    return !notRunning;
}

export const WorkflowContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Save to Template ...', iconClass: 'fa-save', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_WORKFLOWS, permissionActions: [ActionType.VIEW] })],
        enabled: (item: GenericTreeNode | Workflow) => (!(item as any).forceDisable) && ((item instanceof GenericTreeNode && item.data instanceof Workflow && !item.data?.lockedBy) ||
        (item instanceof Workflow && !item.lockedBy))
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Copy ...', iconClass: 'fa-copy', iconColor: 'color-grey-10',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.CREATE] })],
        enabled: (item: GenericTreeNode | Workflow) => (!(item as any).forceDisable)
    }),
    new ContextMenuOption({
        name: 'Lock', iconClass: 'fa-lock', iconColor: 'color-grey-10',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.LOCK_UNLOCK_WORKFLOW, permissionActions: [ActionType.CREATE] })],
        visible: (item: GenericTreeNode | Workflow) =>
            (item instanceof GenericTreeNode && item.data instanceof Workflow && !item.data.lockedBy) ||
            (item instanceof Workflow && !item.lockedBy),
        enabled: (item: GenericTreeNode | Workflow) => (!(item as any).forceDisable)
    }),
    new ContextMenuOption({
        name: 'Unlock', iconClass: 'fa-unlock', iconColor: 'color-grey-10',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.LOCK_UNLOCK_WORKFLOW, permissionActions: [ActionType.CREATE] })],
        visible: (item: GenericTreeNode | Workflow) =>
            (item instanceof GenericTreeNode && item.data instanceof Workflow && item.data.lockedBy !== null) ||
            (item instanceof Workflow && item.lockedBy !== null),
        enabled: (item: GenericTreeNode | Workflow) => (!(item as any).forceDisable)
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.CREATE] })],
        enabled: (item: GenericTreeNode | Workflow) =>
        (!isWorkFlowRunning(item) &&
            ((item instanceof GenericTreeNode && !(item as any).forceDisable && item.data instanceof Workflow && !item.data.lockedBy) ||
                (item instanceof Workflow && !item.lockedBy)))
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.DELETE] })],
        enabled: (item: GenericTreeNode | Workflow) =>
        (!isWorkFlowRunning(item) &&
            ((item instanceof GenericTreeNode && !(item as any).forceDisable && item.data instanceof Workflow && !item.data.lockedBy) ||
                (item instanceof Workflow && !item.lockedBy)))

    })
];

export const WorkflowStepContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Add Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] })],
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        visible: (item: GenericTreeNode) => (item.data as WorkflowStep).task === 'VECTOR_SET_SEARCH'
    }),
    new ContextMenuOption({
        name: 'Remove Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] })],
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        visible: (item: GenericTreeNode) => (item.data as WorkflowStep).task === 'VECTOR_SET_SEARCH'
    }),
    new ContextMenuOption({
        divider: true,
        visible: (item: GenericTreeNode) => (item.data as SearchResult).task !== SearchType.BULK
    }),
    new ContextMenuOption({
        name: 'Add to ...', iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        atLeastOne: true
    }),
    new ContextMenuOption({
        name: 'Remove from ...', iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        atLeastOne: true
    }),
    new ContextMenuOption({
        name: 'Remove from Project Data', iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] })],
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Find Exact Duplicates', iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0
    }),
    new ContextMenuOption({
        name: 'Find Content Duplicates', iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0
    }),
    new ContextMenuOption({
        name: 'Save Search ...',
        iconClass: 'fa-save', iconColor: 'color-tan-07',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SAVED_SEARCH_QUERY, permissionActions: [ActionType.CREATE] })],
    }),
    new ContextMenuOption({
        divider: true,
    }),
    new ContextMenuOption({
        name: 'Calculate Word List',
        iconClass: 'fa-font', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-refresh', iconSecondColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0
    }),
    new ContextMenuOption({
        name: 'View Word List ...', iconClass: 'fa-font', iconColor: 'color-blue-01', tooltip: 'Please Calculate Word List to view it'
    }),
    new ContextMenuOption({
        divider: true,
    }),
    new ContextMenuOption({
        name: 'Create Manifest ...', iconClass: 'fa-list-alt', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_REPORTS })],

    }),
    new ContextMenuOption({
        name: 'Create Comparison ...',
        iconClass: 'fa-exchange', iconColor: 'color-purple-02',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Create Sample ...',
        iconClass: 'fa-eyedropper', iconColor: 'color-tan-07',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0
    }),
    new ContextMenuOption({
        name: 'Create Export Comparison Report ...',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.EXPORT_STREAM })],

    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Download All as PDFs', iconClass: 'fa-file-pdf-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_PDF }]
    }),
    new ContextMenuOption({
        name: 'Download All Natives', iconClass: 'fa-file', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_NATIVES }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Reprocess ...',
        iconClass: 'fa-retweet', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] })],
    }),
    new ContextMenuOption({
        name: 'Copy for External Imaging ...',
        enabled: (item: GenericTreeNode) => (item.data as WorkflowStep).numberResults > 0,
        permissions: [{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.CONNECTOR_ACCESS }],
        atLeastOne: true
    })
];

export const WorkflowMenu = new GenericTreeNode({
    name: 'Workflows',
    entityType: EntityType.workFlow,
    automationId: 'nav-tree-workflows',
    permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW })],
    navigationInfo: new NavigationInfo({ name: 'Summary', path: 'summary', default: true }),
    isExpandable: true,
    isExpandedOnSelect: true,
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.workFlowManager_getWorkFlows,
        responseName: 'workFlows',
        data: {
            startIndex: 0,
            count: 0
        }
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Workflow(data);
        const stepTaskHandles = !data.queryItems ? null : _.filter(_.map(data.queryItems.items, (value: any) => {
            return value.responseHandle;
        }), (handle) => handle !== null);
        const runningAttr = _.find(item.attributes, (attribute) => attribute.name === 'isRunning');
        const isRunning = runningAttr && runningAttr.value && runningAttr.value === 'true';
        return new GenericTreeNode({
            name: item.name,
            automationId: 'workflow-' + item.name,
            entityType: EntityType.workFlow,
            childPadding: 0,
            icon: isRunning ? 'fa-spinner fa-spin' : (item.hasRunError ? 'fa-exclamation-circle' : 'fa-list-ol'),
            iconColor: item.hasRunError ? 'color-red-05' : 'color-green-07',
            iconTitle: item.hasRunError ? 'Unexpected run error. Please re-run the workflow.' : null,
            contextMenuOptions: WorkflowContextMenuOptions,
            onContextMenuAction: WorkflowMenu.onContextMenuAction,
            isExpandable: true,
            isExpandedOnSelect: true,
            hasExpanderPadding: true,
            navigationInfo: new NavigationInfo(
                { name: 'Content', path: 'workflow', default: true },
                { name: 'Reports', path: 'reports' }
            ),
            data: item,
            requestInfo: new DrRequestInfo({
                data: { handle: item.handle },
                endpoint: Endpoint.workFlowManager_getWorkFlow,
                responseName: 'workFlow.queryItems.items'
            }),
            secondRequestInfo: new DrRequestInfo({
                data: { taskHandles: stepTaskHandles, includeDrDebug: false, includeResourceStatistics: false },
                endpoint: Endpoint.taskManager_getTasks,
                responseName: 'tasks',
            }),

            createChild: (stepData: any, childHandler?: any): GenericTreeNode => {
                const stepItem = new WorkflowStep(stepData);
                const viewTypeAttr = _.find(stepItem.attributes, attr => attr.name === 'viewtype');
                if (viewTypeAttr) {
                    stepItem.viewType = viewTypeAttr.value;
                }
                const disabled = !(stepItem.percentComplete > 0) || item.hasRunError;
                const stepType = _.find(stepItem.attributes, attr => attr.name === 'steptype');
                const name = stepType ? stepType.value : 'Term Query';
                return new GenericTreeNode({
                    name: name,
                    customNumber: item.hasRunError ? undefined : stepData.numberResults,
                    automationId: stepItem.handle,
                    entityType: EntityType.searchResult,
                    iconStringContent: stepItem.orderOfExecution ? stepItem.orderOfExecution.toString(10) : '1',
                    isExpandable: false,
                    hasExpanderPadding: true,
                    data: stepItem,
                    contextMenuOptions: WorkflowStepContextMenuOptions,
                    onContextMenuAction: WorkflowMenu.onContextMenuAction,
                    navigationInfo: new NavigationInfo(
                        { name: 'All Docs', path: 'document-list', default: true },
                        { name: 'Email Threads', path: 'email-thread' },
                        { name: 'Short Message Threads', path: 'short-message-thread' },
                        { name: 'Communication Grid', path: 'communication-matrix' },
                        { name: 'Domain Grid', path: 'domain-matrix' },
                        { name: 'Reports', path: 'reports' },
                        { name: 'Clusters', path: 'cluster-list' }
                    ),
                    isDisabled: disabled
                }, childHandler);
            }

        }, handler);
    },

    showContextMenuButton: true,
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Workflow ...',
            iconClass: 'fa-plus',
            iconColor: 'color-green-04', permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({
            name: 'Load from Template ...',
            iconClass: 'fa-folder-open',
            iconColor: 'color-yellow-07', permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_WORKFLOWS, permissionActions: [ActionType.CREATE] })]
        })
    ]
});
